import { EInvoiceAddressSchemeType, EntityType, isValidEInvoiceAddressFormat } from '@components'

import { TFunction } from 'i18next'
import isEmpty from 'lodash/isEmpty'
import { z } from 'zod'

import { CustomerType } from '@features/contacts/enums/customerType'

import { EInvoicingAddressFormSchema } from '../types/eInvoicingAddressFormSchema'
import { getEInvoicingAddressFieldErrorMessage } from './getEInvoicingAddressFieldErrorMessage'
import { normalizeAddressingLine } from './normalizeAddressingLine'

export const getEInvoicingAddressFormValidationSchema = (
  t: TFunction,
  hasFailedAttachments: boolean,
  isFrenchComplianceMode: boolean,
): EInvoicingAddressFormSchema => {
  return z
    .object({
      address: z.object({
        country: z.string(),
      }),
      attachmentUuids: z
        .array(z.string())
        .refine(() => !hasFailedAttachments, {
          message: t('external_invoices.send_as_e_invoice.modal.e_invoicing_address.faulty_attachment_error'),
        })
        .optional(),
      companyName: z.string(),
      customerType: z.nativeEnum(CustomerType),
      eInvoiceAddress: z.object({
        address: z
          .string()
          .trim()
          .transform((address) => address.replace(/\s/g, ''))
          .optional(),
        scheme: z.nativeEnum(EInvoiceAddressSchemeType).optional(),
      }),
      entityType: z.nativeEnum(EntityType).optional(),
      id: z.string(),
      addressingLine: z.string().trim().transform(normalizeAddressingLine).optional(),
    })
    .superRefine((data, ctx) => {
      const address = data.eInvoiceAddress?.address
      const scheme = data.eInvoiceAddress?.scheme
      const country = data.address?.country

      if (isFrenchComplianceMode) {
        if (isEmpty(data.addressingLine)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['addressingLine'],
            message: t('external_invoices.send_as_e_invoice.modal.field.addressing_line.error.required'),
          })
        }

        return
      }

      if ((scheme && address && !isValidEInvoiceAddressFormat(address, scheme, country)) || isEmpty(address)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['eInvoiceAddress.address'],
          message: getEInvoicingAddressFieldErrorMessage(t, scheme),
        })
      }
    })
}
